import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchTrainers } from "../pages/services/trainerService";
import "../styles/ListPage.css";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const TrainerList = () => {
  const navigate  = useNavigate();
  const location  = useLocation();

  const [trainers,    setTrainers]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState("");
  const [successMsg,  setSuccessMsg]  = useState(location.state?.successMessage || null);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const loadTrainers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTrainers();
      setTrainers(data);
    } catch {
      setError("Could not load trainers. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrainers(); }, [loadTrainers]);

  const filtered = trainers.filter(t =>
    t.trainerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(t.contactNumber || "").includes(searchTerm)
  );

  return (
    <div className="lp-page">

      {successMsg && (
        <div className="success-toast" style={{ marginBottom: 12 }}>{successMsg}</div>
      )}

      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">Trainers</h2>
          <span className="lp-count">{filtered.length} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={() => navigate("/trainers/add")}>
            + Add Trainer
          </button>
        </div>
      </div>

      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search by name or mobile…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
      </div>

      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading trainers…</p></div>
        ) : error ? (
          <div className="lp-state"><p style={{ color: "#ef4444" }}>{error}</p></div>
        ) : filtered.length === 0 ? (
          <div className="lp-state">
            <p>{trainers.length === 0 ? "No trainers added yet." : "No trainers match your search."}</p>
          </div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th>Trainer Name</th>
                <th>Mobile Number</th>
                <th style={{ textAlign: "center" }}>Learning Topics</th>
                <th style={{ textAlign: "center" }}>Batches</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(trainer => {
                const isActive   = trainer.isActive !== false;
                const tracking   = trainer.trackingEnabled !== false;
                return (
                  <tr
                    key={trainer._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/trainers/${trainer._id}`, { state: { trainer } })}
                  >
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, color: "#0f172a" }}>{trainer.trainerName}</span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                          textTransform: "uppercase", letterSpacing: 0.4,
                          background: isActive ? "#dcfce7" : "#fee2e2",
                          color:      isActive ? "#15803d" : "#dc2626",
                        }}>
                          {isActive ? "Active" : "Disabled"}
                        </span>
                        {!tracking && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20,
                            background: "#f1f5f9", color: "#64748b",
                            textTransform: "uppercase", letterSpacing: 0.4,
                          }}>
                            No Tracking
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ color: "#475569" }}>
                      {trainer.contactNumber ? String(trainer.contactNumber) : <span style={{ color: "#94a3b8" }}>—</span>}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", minWidth: 28, padding: "2px 8px",
                        background: "#ede9fe", color: "#6d28d9",
                        borderRadius: 20, fontSize: 13, fontWeight: 700,
                      }}>
                        {(trainer.assignedSubjects || []).length}
                      </span>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span style={{
                        display: "inline-block", minWidth: 28, padding: "2px 8px",
                        background: "#e0f2fe", color: "#0369a1",
                        borderRadius: 20, fontSize: 13, fontWeight: 700,
                      }}>
                        {(trainer.assignedBatches || []).length}
                      </span>
                    </td>
                    <td style={{ fontSize: 13, color: "#64748b" }}>
                      {fmtDateTime(trainer.lastActivityAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TrainerList;
